import { Component, OnInit, forwardRef } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';

import { EmployeeGroupMemberOptionsService } from '../../../core/services/employee-group-member-options';
import { AutocompleteOption } from '../autocomplete-select/autocomplete-select.component';

export interface EmployeeGroupMemberFilter {
  group: string;
  id: string | null;
}

export const EMPLOYEE_GROUP_MEMBER_TYPES = [
  { value: 'all', label: 'All employees' },
  { value: 'branch', label: 'Branch' },
  { value: 'employment_position', label: 'Employment position' },
  { value: 'branch_type', label: 'Branch type' },
  { value: 'department', label: 'Department' },
  { value: 'employment', label: 'Employee' },
  { value: 'team', label: 'Team' },
] as const;

export const EMPLOYEE_GROUP_FILTER_OPTION_TYPES = EMPLOYEE_GROUP_MEMBER_TYPES
  .map((type) => type.value)
  .filter((type) => type !== 'all');

const TERMINAL_MEMBER_FILTER_TYPES = new Set(['all', 'employment']);

@Component({
  selector: 'app-employee-group-member-filters-input',
  standalone: false,
  templateUrl: './employee-group-member-filters-input.html',
  styleUrl: './employee-group-member-filters-input.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EmployeeGroupMemberFiltersInput),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => EmployeeGroupMemberFiltersInput),
      multi: true,
    },
  ],
})
export class EmployeeGroupMemberFiltersInput implements ControlValueAccessor, Validator, OnInit {
  readonly memberTypes = EMPLOYEE_GROUP_MEMBER_TYPES;
  filters: EmployeeGroupMemberFilter[] = [];
  optionsByType: Record<string, AutocompleteOption[]> = {};
  loading = true;
  loadError = '';
  disabled = false;
  touched = false;

  private onChange = (_value: EmployeeGroupMemberFilter[]) => {};
  private onTouched = () => {};
  private onValidatorChange = () => {};

  constructor(private readonly memberOptions: EmployeeGroupMemberOptionsService) {}

  ngOnInit(): void {
    setTimeout(() => void this.loadOptions(), 0);
  }

  writeValue(value: EmployeeGroupMemberFilter[] | null): void {
    this.filters = (value ?? []).map((filter) => ({
      group: filter.group,
      id: filter.group === 'all' ? null : filter.id ?? '',
    }));
    this.onValidatorChange();
  }

  registerOnChange(fn: (value: EmployeeGroupMemberFilter[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    if (this.filters.length === 0) {
      return { memberFiltersRequired: true };
    }
    const groups = this.filters.map((filter) => filter.group);
    if (new Set(groups).size !== groups.length) {
      return { duplicateMemberFilterType: true };
    }
    if (groups.some((group) => TERMINAL_MEMBER_FILTER_TYPES.has(group)) && groups.length > 1) {
      return { terminalMemberFilterCannotIntersect: true };
    }
    if (this.filters.some((filter) => filter.group !== 'all' && !filter.id)) {
      return { memberFilterRecordRequired: true };
    }
    return null;
  }

  addFilter(): void {
    if (!this.canAddFilter) return;
    const used = new Set(this.filters.map((filter) => filter.group));
    const group = EMPLOYEE_GROUP_FILTER_OPTION_TYPES.find((type) => !used.has(type));
    if (!group) return;
    this.filters = [...this.filters, { group, id: '' }];
    this.emitValue();
  }

  removeFilter(index: number): void {
    if (this.disabled) return;
    this.filters = this.filters.filter((_, filterIndex) => filterIndex !== index);
    this.emitValue();
  }

  changeType(index: number, group: string): void {
    this.filters = this.filters.map((filter, filterIndex) => filterIndex === index
      ? { group, id: group === 'all' ? null : '' }
      : filter);
    this.emitValue();
  }

  changeRecord(index: number, id: string): void {
    this.filters = this.filters.map((filter, filterIndex) => filterIndex === index
      ? { ...filter, id }
      : filter);
    this.emitValue();
  }

  options(group: string): AutocompleteOption[] {
    return group === 'all' ? [] : this.optionsByType[group] ?? [];
  }

  isTypeUnavailable(type: string, index: number): boolean {
    if (this.filters[index]?.group === type) return false;
    if (TERMINAL_MEMBER_FILTER_TYPES.has(type)) return this.filters.length > 1;
    return this.filters.some((filter, filterIndex) => filterIndex !== index && filter.group === type);
  }

  get canAddFilter(): boolean {
    if (this.disabled || this.filters.some((filter) => TERMINAL_MEMBER_FILTER_TYPES.has(filter.group))) {
      return false;
    }
    const used = new Set(this.filters.map((filter) => filter.group));
    return EMPLOYEE_GROUP_FILTER_OPTION_TYPES.some((type) => !used.has(type));
  }

  get validationMessage(): string {
    if (this.filters.length === 0) return 'Add at least one member filter.';
    const groups = this.filters.map((filter) => filter.group);
    if (new Set(groups).size !== groups.length) return 'Each member type can only be used once.';
    if (groups.some((group) => TERMINAL_MEMBER_FILTER_TYPES.has(group)) && groups.length > 1) {
      return 'All employees and a specific employee must each be used as a rule on their own.';
    }
    if (this.filters.some((filter) => filter.group !== 'all' && !filter.id)) {
      return 'Select a record for every member filter.';
    }
    return '';
  }

  markTouched(): void {
    this.touched = true;
    this.onTouched();
  }

  private async loadOptions(): Promise<void> {
    this.loading = true;
    this.loadError = '';
    try {
      const entries = await Promise.all(EMPLOYEE_GROUP_FILTER_OPTION_TYPES.map(async (type) => [
        type,
        await this.memberOptions.options(type),
      ] as const));
      this.optionsByType = Object.fromEntries(entries);
    } catch {
      this.loadError = 'Unable to load employee group member options.';
    } finally {
      this.loading = false;
    }
  }

  private emitValue(): void {
    this.markTouched();
    const value = this.filters.map((filter) => ({ ...filter }));
    this.onChange(value);
    this.onValidatorChange();
  }

}
